import * as React from "react";
import {
  CheckCircle2,
  ChevronsLeft,
  ChevronsRight,
  Pause,
  Play,
} from "lucide-react";

/**
 * The seek and moment controls below the watch page player, in v2 dress.
 *
 * Padding and gaps are inline because the globally loaded Bootstrap reset
 * zeroes Tailwind padding utilities inside `.v2`, and zeroes margins on
 * buttons and paragraphs. Spacing therefore comes from flex and grid `gap` on
 * container `div`s, never from margins on the children.
 */

export type ClipBarProps = {
  handleClipPost: (name: string) => void;
  rewind: (seconds: number) => void;
  skipForward: (seconds: number) => void;
  playVideo: () => void;
  pauseVideo: () => void;
  running: boolean;
};

const SEEK_BACK = [10, 3, 1];
const SEEK_FORWARD = [1, 3, 10];

const MOMENTS: Array<{ label: string; value: string }> = [
  { label: "GOAL", value: "goal" },
  { label: "SHOT", value: "shot" },
  { label: "SAVE", value: "save" },
  { label: "HIGHLIGHT", value: "highlight" },
  { label: "FACEOFF", value: "faceoff" },
  { label: "HIT", value: "hit" },
  { label: "PENALTY", value: "penalty" },
  { label: "CUSTOM", value: "custom" },
];

const seconds = (n: number) => (n === 1 ? "1 second" : `${n} seconds`);

const controlClass =
  "flex items-center justify-center gap-1 rounded-lg border border-slate-200 bg-white " +
  "text-sm font-semibold uppercase tracking-wide transition-colors " +
  "hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2";

const controlStyle: React.CSSProperties = {
  padding: "10px 8px",
  color: "var(--v2-primary)",
  outlineColor: "var(--v2-ring)",
};

export function ClipBar({
  handleClipPost,
  rewind,
  skipForward,
  playVideo,
  pauseVideo,
  running,
}: ClipBarProps) {
  return (
    <div
      className="flex flex-col border border-slate-200 bg-white"
      style={{ padding: "16px", gap: "12px" }}
    >
      <div className="grid grid-cols-7" style={{ gap: "8px" }}>
        {SEEK_BACK.map((n) => (
          <button
            key={`back-${n}`}
            type="button"
            className={controlClass}
            style={controlStyle}
            aria-label={`Rewind ${seconds(n)}`}
            onClick={() => rewind(n)}
          >
            <ChevronsLeft className="size-4" aria-hidden="true" />
            <span>{n}</span>
          </button>
        ))}

        <button
          type="button"
          className={controlClass}
          style={controlStyle}
          aria-label={running ? "Pause" : "Play"}
          onClick={() => (running ? pauseVideo() : playVideo())}
        >
          {running ? (
            <Pause className="size-4" aria-hidden="true" />
          ) : (
            <Play className="size-4" aria-hidden="true" />
          )}
        </button>

        {SEEK_FORWARD.map((n) => (
          <button
            key={`forward-${n}`}
            type="button"
            className={controlClass}
            style={controlStyle}
            aria-label={`Forward ${seconds(n)}`}
            onClick={() => skipForward(n)}
          >
            <span>{n}</span>
            <ChevronsRight className="size-4" aria-hidden="true" />
          </button>
        ))}
      </div>

      <div className="text-center text-xs font-semibold tracking-widest text-slate-500">
        CREATE MOMENT
      </div>

      <div
        className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8"
        style={{ gap: "8px" }}
      >
        {MOMENTS.map((moment) => (
          <button
            key={moment.value}
            type="button"
            className={controlClass}
            style={controlStyle}
            onClick={() => handleClipPost(moment.value)}
          >
            {moment.label}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-center text-xs text-slate-500" style={{ gap: "6px" }}>
        <CheckCircle2 className="size-4 shrink-0" aria-hidden="true" />
        <span>
          Moments are saved and available in your Moments library in the Audience
          Layer.
        </span>
      </div>
    </div>
  );
}
